import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Breadcrumb, Card, Spinner, Alert, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  HomeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  FileTextOutlined,
  TrophyOutlined,
  CalendarOutlined,
  RiseOutlined,
  FireOutlined,
  SyncOutlined,
  SendOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Statistic, Progress, Tag, Avatar, Space, Empty } from "antd";
import { fetchUserDashboardOverview } from "@/store/dashboardApi/dashboardThunk";
import formatDate from "@/utils/formatDate";
import { CalendarView, TaskDateModal } from "@/components/Calendar";

const UserDashboard = () => {
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const {
    userOverview,
    userOverviewLoading,
    userOverviewError,
  } = useSelector((state) => state.dashboard);

  useEffect(() => {
    dispatch(fetchUserDashboardOverview());
  }, [dispatch]);

  const stats = userOverview?.stats;
  const performance = userOverview?.performance;
  const recentTasks = userOverview?.recent_tasks || [];
  const recentActivity = userOverview?.recent_activity || [];
  const events = userOverview?.events || [];
  const user = userOverview ? {
    name: userOverview.name,
    picture: userOverview.picture,
    roles: userOverview.roles,
  } : null;

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { 
        bg: "warning", 
        text: "Kutilmoqda", 
        icon: <ClockCircleOutlined style={{ marginRight: 4 }} /> 
      },
      in_progress: { 
        bg: "info", 
        text: "Jarayonda", 
        icon: <SyncOutlined spin style={{ marginRight: 4 }} /> 
      },
      submitted: { 
        bg: "primary", 
        text: "Topshirildi", 
        icon: <SendOutlined style={{ marginRight: 4 }} /> 
      },
      approved: { 
        bg: "success", 
        text: "Tasdiqlandi", 
        icon: <CheckCircleOutlined style={{ marginRight: 4 }} /> 
      },
      rejected: { 
        bg: "danger", 
        text: "Rad etildi", 
        icon: <CloseCircleOutlined style={{ marginRight: 4 }} /> 
      },
    };
    return statusMap[status] || { 
      bg: "secondary", 
      text: status, 
      icon: <FileTextOutlined style={{ marginRight: 4 }} /> 
    };
  };

  const activityTypeMap = {
    login: "Tizimga kirdi",
    task_create: "Topshiriq yaratdi",
    task_complete: "Topshiriq bajardi",
    task_submit: "Topshiriq topshirdi",
    task_approve: "Topshiriq tasdiqladi",
    task_reject: "Topshiriq rad etdi",
    task_start: "Topshiriqni boshladi",
  };

  const eventStatusMap = {
    upcoming: { 
      color: "blue", 
      text: "Kelgusi", 
      icon: <CalendarOutlined style={{ marginRight: 4 }} /> 
    },
    ongoing: { 
      color: "orange", 
      text: "Davom etmoqda", 
      icon: <ExclamationCircleOutlined style={{ marginRight: 4 }} /> 
    },
    completed: { 
      color: "green", 
      text: "Yakunlangan", 
      icon: <CheckCircleOutlined style={{ marginRight: 4 }} /> 
    },
    overdue: { 
      color: "red", 
      text: "Muddati o'tgan", 
      icon: <WarningOutlined style={{ marginRight: 4 }} /> 
    },
  };

  const getEventStatusTag = (status) => eventStatusMap[status] || { 
    color: "default", 
    text: status || "Noma'lum", 
    icon: <ExclamationCircleOutlined style={{ marginRight: 4 }} /> 
  };

  const getEventTaskStatusTag = (status) =>
    status === "completed"
      ? { color: "green", text: "Bajarilgan" }
      : { color: "orange", text: "Bajarilmagan" };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString("uz-UZ", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isPastEvent = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowTaskModal(true);
  };

  const handleModalClose = () => {
    setShowTaskModal(false);
    setSelectedDate(null);
  };

  // Priority tasks (overdue + in progress)
  const priorityTasks = recentTasks.filter(t => t.is_overdue || t.status === 'in_progress').slice(0, 3);
  const upcomingEvents = events.filter(e => e.status === 'upcoming').slice(0, 3);

  if (userOverviewLoading && !userOverview) {
    return (
      <Container fluid className="p-4 d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (userOverviewError) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">
          <Alert.Heading>Xatolik yuz berdi</Alert.Heading>
          <p>{userOverviewError}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Shaxsiy Dashboard</Breadcrumb.Item>
      </Breadcrumb>

      {/* Welcome Card */}
      {user && (
        <Card className="mb-4 border-0 shadow-sm" >
          <Card.Body className="p-4">
            <Row className="align-items-center">
              <Col xs="auto">
                <Avatar
                  size={80}
                  src={user.picture}
                  icon={!user.picture && <i className="fa fa-user"></i>}
                  style={{ border: "4px solid rgba(255,255,255,0.3)" }}
                />
              </Col>
              <Col>
                <h3 className="text-dark mb-2">Xush kelibsiz, {user.name}! 👋</h3>
                <div className="d-flex flex-wrap gap-2">
                  {user.roles?.map((role, idx) => (
                    <Tag key={idx} color="purple" style={{ fontSize: "13px", padding: "4px 12px" }}>
                      {role}
                    </Tag>
                  ))}
                </div>
              </Col>
              <Col xs={12} md="auto" className="mt-3 mt-md-0">
                <div className="text-dark text-md-end">
                  <div style={{ fontSize: "14px", opacity: 0.9 }}>Bugun</div>
                  <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                    {new Date().toLocaleDateString("uz-UZ")}
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Quick Stats - 4 cards */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: "13px" }}>Jami topshiriqlar</p>
                  <h3 className="mb-0" style={{ color: "#1890ff" }}>{stats?.tasks_assigned || 0}</h3>
                </div>
                <div style={{ fontSize: "32px", opacity: 0.2 }}>
                  <FileTextOutlined />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: "13px" }}>Jarayonda</p>
                  <h3 className="mb-0" style={{ color: "#faad14" }}>{stats?.tasks_in_progress || 0}</h3>
                </div>
                <div style={{ fontSize: "32px", opacity: 0.2 }}>
                  <ClockCircleOutlined />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: "13px" }}>Bajarilgan</p>
                  <h3 className="mb-0" style={{ color: "#52c41a" }}>{stats?.tasks_completed || 0}</h3>
                </div>
                <div style={{ fontSize: "32px", opacity: 0.2 }}>
                  <CheckCircleOutlined />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: "13px" }}>Muddati o'tgan</p>
                  <h3 className="mb-0" style={{ color: "#ff4d4f" }}>{stats?.tasks_overdue || 0}</h3>
                </div>
                <div style={{ fontSize: "32px", opacity: 0.2 }}>
                  <WarningOutlined />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Priority Section */}
      <Row className="g-3 mb-4">
        <Col xs={12} lg={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <FireOutlined style={{ fontSize: "20px", color: "#ff4d4f", marginRight: "8px" }} />
                <h5 className="mb-0">Muhim topshiriqlar</h5>
              </div>
              {priorityTasks.length > 0 ? (
                <div>
                  {priorityTasks.map((task) => {
                    const statusBadge = getStatusBadge(task.status);
                    return (
                      <Link
                        key={task.id}
                        to={`/tasks/my-detail/${task.id}`}
                        className="text-decoration-none"
                      >
                        <div className="p-3 mb-2 border rounded hover-shadow" style={{ transition: "all 0.2s" }}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <h6 className="mb-1 text-dark">{statusBadge.icon} {task.title}</h6>
                              <small className="text-muted">
                                {task.deadline && `Muddat: ${formatDate(task.deadline)}`}
                              </small>
                            </div>
                            {task.is_overdue && (
                              <Badge bg="danger" className="ms-2">Kechikkan</Badge>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <Empty 
                  description="Muhim topshiriqlar yo'q" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ padding: "40px 0" }}
                />
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <CalendarOutlined style={{ fontSize: "20px", color: "#1890ff", marginRight: "8px" }} />
                <h5 className="mb-0">Yaqinlashayotgan tadbirlar</h5>
              </div>
              {upcomingEvents.length > 0 ? (
                <div>
                  {upcomingEvents.map((event) => {
                    const statusConfig = getEventStatusTag(event.status);
                    return (
                      <Link
                        key={event.id}
                        to={`/events/detail/${event.id}`}
                        className="text-decoration-none"
                      >
                        <div className="p-3 mb-2 border rounded hover-shadow" style={{ transition: "all 0.2s" }}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 className="mb-1 text-dark">{statusConfig.icon} {event.name}</h6>
                              <small className="text-muted">
                                {formatDate(event.datetime)} • {formatTime(event.datetime)}
                              </small>
                            </div>
                            <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <Empty 
                  description="Yaqinlashayotgan tadbirlar yo'q" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ padding: "40px 0" }}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Performance Cards */}
      <Row className="g-3 mb-4">
        <Col xs={12} lg={8}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <TrophyOutlined style={{ fontSize: "20px", color: "#faad14", marginRight: "8px" }} />
                <h5 className="mb-0">Ishlash ko'rsatkichlari</h5>
              </div>
              
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <div className="p-3 border rounded">
                    <p className="text-muted mb-2 small">Bajarilish foizi</p>
                    <Progress
                      percent={Math.round(performance?.completion_rate || 0)}
                      strokeColor="#52c41a"
                      strokeWidth={12}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="p-3 border rounded">
                    <p className="text-muted mb-2 small">Muddatida bajarilish</p>
                    <Progress
                      percent={Math.round(performance?.on_time_rate || 0)}
                      strokeColor="#1890ff"
                      strokeWidth={12}
                    />
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="text-center p-3 border rounded">
                    <RiseOutlined style={{ fontSize: "24px", color: "#52c41a" }} />
                    <h4 className="mt-2 mb-0">{performance?.tasks_completed_this_week || 0}</h4>
                    <small className="text-muted">Bu hafta</small>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="text-center p-3 border rounded">
                    <CheckCircleOutlined style={{ fontSize: "24px", color: "#1890ff" }} />
                    <h4 className="mt-2 mb-0">{performance?.tasks_completed_this_month || 0}</h4>
                    <small className="text-muted">Bu oy</small>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="text-center p-3 border rounded">
                    <ClockCircleOutlined style={{ fontSize: "24px", color: "#faad14" }} />
                    <h4 className="mt-2 mb-0">{performance?.avg_completion_time_hours?.toFixed(1) || "0"}</h4>
                    <small className="text-muted">O'rtacha soat</small>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="text-center p-3 border rounded">
                    <TrophyOutlined style={{ fontSize: "24px", color: "#faad14" }} />
                    <h4 className="mt-2 mb-0">{performance?.avg_rating?.toFixed(1) || "N/A"}</h4>
                    <small className="text-muted">Reyting ⭐</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={4}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Qo'shimcha</h5>
              <div className="d-grid gap-3">
                <div className="d-flex justify-content-between align-items-center p-3 border rounded">
                  <span className="text-muted">Tasdiqlash uchun</span>
                  <h4 className="mb-0" style={{ color: "#722ed1" }}>{stats?.tasks_to_approve || 0}</h4>
                </div>
                <div className="d-flex justify-content-between align-items-center p-3 border rounded">
                  <span className="text-muted">Men yaratgan</span>
                  <h4 className="mb-0" style={{ color: "#13c2c2" }}>{stats?.tasks_created || 0}</h4>
                </div>
                <div className="d-flex justify-content-between align-items-center p-3 border rounded">
                  <span className="text-muted">O'qilmagan eventlar</span>
                  <h4 className="mb-0" style={{ color: "#fa8c16" }}>{stats?.events_unread || 0}</h4>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Calendar */}
      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">
                <CalendarOutlined className="me-2" />
                Topshiriqlar kalendari
              </h5>
              <CalendarView onDateClick={handleDateClick} isAdmin={false} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Tasks & Activity */}
      <Row className="g-3 mb-4">
        <Col xs={12} lg={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Oxirgi topshiriqlar</h5>
              {recentTasks.length > 0 ? (
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {recentTasks.map((task) => {
                    const statusBadge = getStatusBadge(task.status);
                    return (
                      <Link
                        key={task.id}
                        to={`/tasks/my-detail/${task.id}`}
                        className="text-decoration-none"
                      >
                        <div className="p-3 mb-2 border-bottom hover-bg-light">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <h6 className="mb-1 text-dark">{task.title}</h6>
                              <small className="text-muted">
                                {task.deadline && `${formatDate(task.deadline)}`}
                              </small>
                            </div>
                            <div className="ms-2 text-center">
                              <Badge bg={statusBadge.bg} style={{ minWidth: "100px" }}>
                                {statusBadge.text}
                              </Badge>
                              {task.is_overdue && (
                                <div className="mt-1">
                                  <Tag color="red" style={{ fontSize: "11px" }}>Kechikkan</Tag>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <Empty 
                  description="Topshiriqlar mavjud emas" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ padding: "60px 0" }}
                />
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Faoliyat tarixi</h5>
              {recentActivity.length > 0 ? (
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {recentActivity.map((activity, idx) => (
                    <div key={activity.id || idx} className="p-3 mb-2 border-bottom">
                      <p className="mb-1 fw-medium">
                        {activityTypeMap[activity.activity_type] || activity.activity_type}
                      </p>
                      <small className="text-muted">
                        {formatDate(activity.timestamp)}
                      </small>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty 
                  description="Faoliyat mavjud emas" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ padding: "60px 0" }}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <TaskDateModal
        visible={showTaskModal}
        onClose={handleModalClose}
        selectedDate={selectedDate}
      />

      <style jsx>{`
        .hover-shadow:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          transform: translateY(-1px);
        }
        .hover-bg-light:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </Container>
  );
};

export default UserDashboard;